import React, { useEffect, useState } from "react";
import { DownloadOutlined } from "@ant-design/icons";
import { message } from "antd";
import GenericTable from "../../../Components/Tables/GenericTable";
import { getSubscriptionPayments } from "../../../API/Admin/SubscriptionPayments";
import InitialsAvatar from "../../../Components/InitialsAvatar";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveFileUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path) || path.startsWith("blob:")) return path;
  return `${BACKEND_ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;
};

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const formatLabel = (str) =>
  str
    ? str
        .split("_")
        .map((word) => capitalize(word))
        .join(" ")
    : str;

const getErrorMessage = (data, fallback) =>
  data?.message || data?.detail || fallback;

const BADGE_COLORS = [
  { background: "#4f46e51f", color: "#4f46e5" },
  { background: "#fee2e2", color: "#dc2626" },
  { background: "#ecfdf5", color: "#059669" },
  { background: "#ede9fe", color: "#7c3aed" },
  { background: "#f59e0b1f", color: "#f59e0b" },
  { background: "#dbeafe", color: "#3b82f6" },
  { background: "#ea580c1a", color: "#ea580c" },
  { background: "#fdf2f8", color: "#db2777" },
  { background: "#f0fdfa", color: "#0d9488" },
];

const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

const getModuleBadgeStyle = (name) => {
  if (!name || name === "-") {
    return { background: "#f4f4f5", color: "#71717a" };
  }
  if (name.toLowerCase() === "fuel") {
    return { background: "#0000000f", color: "#000000" };
  }
  return BADGE_COLORS[hashString(name) % BADGE_COLORS.length];
};

const mapPayment = (item, index) => ({
  key: String(item.id ?? index),
  date: item.date || "-",
  txnId: item.txn_id || "-",
  fleetManager: item.fleet_manager || "-",
  logo: resolveFileUrl(item.logo),
  vehicles: item.vehicles ?? 0,
  peramount: parseFloat(item.per_amount) || 0,
  amount: parseFloat(item.amount) || 0,
  module: Array.isArray(item.modules)
    ? item.modules.map((mod) => mod.name).join(", ")
    : "-",
  subPlan: item.sub_plan || formatLabel(item.billing_cycle) || "-",
  paymentMethod: formatLabel(item.payment_method) || "-",
  paidBy: item.paid_by || "-",
  proof: item.proof || "-",
  fileUrl: resolveFileUrl(item.file_url),
  status: item.status || "-",
});

const SubscriptionPayments = () => {
  const [subscriptionPaymentData, setSubscriptionPaymentData] = useState([]);
  const [activeModuleFilters, setActiveModuleFilters] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getSubscriptionPayments();
        const payload = res?.data?.data;
        const items = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.results)
            ? payload.results
            : [];

        if (Math.floor(res?.status / 100) === 2) {
          setSubscriptionPaymentData(items.map(mapPayment));
        } else {
          message.error(
            getErrorMessage(res?.data, "Failed to load subscription payments."),
          );
        }
      } catch (err) {
        message.error(
          getErrorMessage(
            err?.response?.data,
            "Failed to load subscription payments.",
          ),
        );
      }
    };

    fetchData();
  }, []);

  const getUniqueFilters = (data, key) =>
    [...new Set(data.map((item) => item[key]).filter((v) => v !== "-"))].map(
      (value) => ({
        text: value,
        value,
      }),
    );

  const moduleFilters = [
    ...new Set(
      subscriptionPaymentData.flatMap((item) => item.module.split(", ")),
    ),
  ].map((value) => ({ text: value, value }));
  const subPlanFilters = getUniqueFilters(subscriptionPaymentData, "subPlan");
  const paymentMethodFilters = getUniqueFilters(
    subscriptionPaymentData,
    "paymentMethod",
  );
  const statusFilters = getUniqueFilters(subscriptionPaymentData, "status");

  const subscriptionPaymentColumns = [
    {
      title: "Date/Time",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) =>
        (new Date(a.date).getTime() || 0) - (new Date(b.date).getTime() || 0),
      render: (text) => {
        const parts = text.split(" ");
        const datePart = parts[0];
        const timePart = parts[1];

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              lineHeight: "1.2",
            }}
          >
            <span>{datePart}</span>
            <span
              style={{
                color: "#71717A",
              }}
            >
              {timePart}
            </span>
          </div>
        );
      },
    },
    {
      title: "Txn Id",
      dataIndex: "txnId",
      key: "txnId",
      enableSearch: "true",
    },
    {
      title: "Fleet Manager",
      dataIndex: "fleetManager",
      key: "fleetManager",
      enableSearch: true,
      render: (text, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <InitialsAvatar
            src={record.logo}
            name={text}
            size={24}
            shape="square"
          />
          {text}
        </span>
      ),
    },
    {
      title: "Vehicles",
      dataIndex: "vehicles",
      key: "vehicles",
      sorter: (a, b) => a.vehicles - b.vehicles,
    },

    {
      title: "Per Veh Amount",
      dataIndex: "peramount",
      key: "peramount",
      sorter: (a, b) => a.peramount - b.peramount,
      render: (amount) => <span style={{ fontWeight: 600 }}>${amount}</span>,
    },
    {
      title: "Total Amount",
      dataIndex: "amount",
      key: "amount",
      sorter: (a, b) => a.amount - b.amount,
      render: (amount) => <span style={{ fontWeight: 600 }}>${amount}</span>,
    },
    {
      title: "Module",
      dataIndex: "module",
      key: "module",
      filters: moduleFilters,
      onFilter: (value, record) => record.module.split(", ").includes(value),
      render: (module) => {
        const names = module.split(", ");
        const visibleNames =
          activeModuleFilters && activeModuleFilters.length
            ? names.filter((name) => activeModuleFilters.includes(name))
            : names;

        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {visibleNames.map((name) => (
              <span
                key={name}
                className="module-badge"
                style={getModuleBadgeStyle(name)}
              >
                {name}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      title: "Sub Plan",
      dataIndex: "subPlan",
      key: "subPlan",
      filters: subPlanFilters,
      onFilter: (value, record) => record.subPlan === value,
    },

    {
      title: "Payment Method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      filters: paymentMethodFilters,
      onFilter: (value, record) => record.paymentMethod === value,
      render: (method) => <span className="fuel-type-badge">{method}</span>,
    },
    {
      title: "Paid By",
      dataIndex: "paidBy",
      key: "paidBy",
      enableSearch: true,
      render: (text, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <InitialsAvatar
            src={record.logo}
            name={text}
            size={24}
            shape="square"
          />
          {text}
        </span>
      ),
    },
    {
      title: "Payment Proof",
      dataIndex: "proof",
      key: "proof",
      render: (_, record) => (
        <button
          className="proof-link"
          onClick={() => {
            if (record.fileUrl) {
              window.open(record.fileUrl, "_blank", "noopener,noreferrer");
            }
          }}
        >
          <span className="proof-icon">
            <DownloadOutlined />
          </span>
          {record.proof}
        </button>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: statusFilters,
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <span className={`status-badge ${status.toLowerCase()}`}>{status}</span>
      ),
    },
  ];

  return (
    <div className="subscription-payments">
      <GenericTable
        title="Subscription Payment History"
        dataSource={subscriptionPaymentData}
        columns={subscriptionPaymentColumns}
        enableRowSelection={false}
        onTableChange={(filters) =>
          setActiveModuleFilters(filters.module || null)
        }
      />
    </div>
  );
};

export default SubscriptionPayments;
